/**
 * Easter posters — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const easterPostersPack: ToilePack = {
    slug: 'easter-posters',
    category: 'season',
    name: 'Easter posters',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'bunny', label: 'Big bunny', subject: 'A large bunny sitting among tulips filling the poster, no text'},
        {key: 'basket', label: 'Basket of eggs', subject: 'A basket overflowing with painted eggs on grass with blossom, no text'},
        {key: 'greeting', label: 'Greeting', subject: 'A poster with "HAPPY EASTER" lettered large in a rounded serif with an egg border and nothing else'},
    ],
    styles: [
        {key: 'pastel', label: 'Pastel kawaii', style: 'pastel kawaii illustration, rounded shapes, soft outlines, friendly faces', palette: 'Soft pink, mint, lilac, butter yellow and cream', framing: 'Centered on a solid pastel background, built to read clean at small size'},
        {key: 'vintage', label: 'Vintage', style: 'victorian easter card illustration, soft halftone grain, gentle colour', palette: 'Faded rose, sage green, cream and a little gold', framing: 'Centered on aged cream paper with an even margin'},
        {key: 'modern', label: 'Modern minimal', style: 'modern minimal illustration, few flat shapes, no outlines', palette: 'Two or three flat spring colours on white', framing: 'Centered with generous white space around it'},
    ],
    directions: ['a chick instead', 'for a shop window display', 'in a brighter pastel palette'],
    directionHint: 'try: a chick instead',
    seo: {
        title: 'Easter Posters: Bunny, Basket & Greeting',
        metaDescription: 'Nine Easter posters — big bunny, basket of eggs, Happy Easter greeting — in pastel kawaii, vintage, and modern minimal styles.',
        h1: 'Easter poster design: bunny, basket of eggs, happy-easter greeting',
        tagline: 'Nine posters, three subjects, three treatments each.',
        intro: 'Nine bold, flat Easter posters, built to read from across a room: a large bunny sitting among tulips, a basket overflowing with painted eggs on grass with blossom, and a Happy Easter greeting in rounded lettering — each in a pastel kawaii, a Victorian vintage, and a modern minimal treatment. Printed bleed to edge for a wall or window.',
        useCases: [
            'a shop or bakery window display',
            'a home wall print for the season',
            'a classroom or community bulletin board',
            'a printable poster for an egg-hunt event',
        ],
        faq: [
            {q: 'What size do these posters print at?', a: 'Each poster is a tall vertical format, bled to the edge, so it scales cleanly from a small print to a full wall poster.'},
            {q: 'Is there any lettering I need to add myself?', a: "The greeting poster already carries 'Happy Easter' lettered in; the bunny and basket posters have no text so they work as pure decor."},
            {q: "What's the difference between the three styles?", a: 'Pastel kawaii uses rounded, friendly shapes in soft colour, vintage draws on Victorian Easter-card illustration, and modern minimal keeps to a few flat shapes and generous space.'},
            {q: 'Can I print these for a shop or bakery?', a: 'Yes — the posters are cleared for commercial use, including storefront and shop display.'},
        ],
    },
}
